import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Topbar from "../components/Topbar";
import useLang from "../hooks/useLang";
import api from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { LANGS } from "../i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Briefcase, Leaf } from "@phosphor-icons/react";

const WORK_EMOJIS = ["🧠", "💼", "⚡", "🎯", "📊", "🛠️", "🚀", "🎨", "📐", "🔬", "💡", "🎓"];
const PERSONAL_EMOJIS = ["🌿", "☕", "🎵", "🎮", "📚", "🏃", "🍕", "✈️", "🌊", "🎬", "🏡", "🐾"];

export default function CloneSetup() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialCategory = params.get("category") === "personal" ? "personal" : "work";
  const [form, setForm] = useState({
    role_name: "",
    industry: "",
    responsibilities: "",
    language: localStorage.getItem("lang") || "it",
    avatar_emoji: initialCategory === "personal" ? "🌿" : "🧠",
    category: initialCategory,
    linked_clone_id: "",
  });
  const [existingClones, setExistingClones] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/clones").then(({ data }) => setExistingClones(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const changeCategory = (cat) => {
    setForm((f) => ({
      ...f,
      category: cat,
      avatar_emoji: cat === "personal" ? "🌿" : "🧠",
      linked_clone_id: "",
    }));
  };

  const EMOJIS = form.category === "personal" ? PERSONAL_EMOJIS : WORK_EMOJIS;
  const linkable = existingClones.filter((c) => c.category !== form.category && !c.linked_clone_id);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.linked_clone_id) delete payload.linked_clone_id;
      const { data } = await api.post("/clones", payload);
      toast.success("Clone creato");
      navigate(`/clone/${data.id}?mode=setup`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 lg:py-16">
        <button onClick={() => navigate("/")} className="overline mb-8 flex items-center gap-2 hover:text-zinc-900 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft size={14} /> {t("back")}
        </button>

        <div className="overline mb-3">/// New Clone</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light mb-3">{t("setupTitle")}</h1>
        <p className="text-zinc-500 mb-10">{t("setupSubtitle")}</p>

        <div className="grid grid-cols-2 gap-3 mb-10">
          <button
            type="button"
            onClick={() => changeCategory("work")}
            data-testid="category-work-btn"
            className={`text-left p-5 border rounded-md transition-all ${form.category === "work" ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 hover:border-zinc-400"}`}
          >
            <Briefcase size={22} weight="duotone" className="mb-3" />
            <div className="font-heading text-lg font-medium">{t("categoryWork")}</div>
            <p className={`text-xs mt-1 ${form.category === "work" ? "text-zinc-300" : "text-zinc-500"}`}>{t("categoryWorkDesc")}</p>
          </button>
          <button
            type="button"
            onClick={() => changeCategory("personal")}
            data-testid="category-personal-btn"
            className={`text-left p-5 border rounded-md transition-all ${form.category === "personal" ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 hover:border-zinc-400"}`}
          >
            <Leaf size={22} weight="duotone" className="mb-3" />
            <div className="font-heading text-lg font-medium">{t("categoryPersonal")}</div>
            <p className={`text-xs mt-1 ${form.category === "personal" ? "text-zinc-300" : "text-zinc-500"}`}>{t("categoryPersonalDesc")}</p>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-8 animate-fade-up">
          <div className="space-y-2">
            <Label className="overline">{form.category === "personal" ? t("yourName") : t("roleName")}</Label>
            <Input
              required
              value={form.role_name}
              onChange={(e) => set("role_name", e.target.value)}
              placeholder={form.category === "personal" ? t("yourNamePh") : t("roleNamePh")}
              data-testid="setup-role-input"
              className="rounded-md border-zinc-200 h-12 text-base"
            />
          </div>
          <div className="space-y-2">
            <Label className="overline">{form.category === "personal" ? t("lifeContext") : t("industry")}</Label>
            <Input
              required
              value={form.industry}
              onChange={(e) => set("industry", e.target.value)}
              placeholder={form.category === "personal" ? t("lifeContextPh") : t("industryPh")}
              data-testid="setup-industry-input"
              className="rounded-md border-zinc-200 h-12 text-base"
            />
          </div>
          <div className="space-y-2">
            <Label className="overline">{form.category === "personal" ? t("personality") : t("responsibilities")}</Label>
            <Textarea
              required
              value={form.responsibilities}
              onChange={(e) => set("responsibilities", e.target.value)}
              placeholder={form.category === "personal" ? t("personalityPh") : t("responsibilitiesPh")}
              rows={6}
              data-testid="setup-responsibilities-input"
              className="rounded-md border-zinc-200 text-base resize-y min-h-[140px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="overline">{t("language")}</Label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger data-testid="setup-language-select" className="rounded-md border-zinc-200 h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGS.map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      <span className="mr-2">{l.flag}</span>{l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="overline">{t("avatar")}</Label>
              <div className="flex flex-wrap gap-2 pt-1">
                {EMOJIS.map((em) => (
                  <button
                    key={em}
                    type="button"
                    onClick={() => set("avatar_emoji", em)}
                    data-testid={`avatar-${em}`}
                    className={`w-10 h-10 rounded-md border flex items-center justify-center text-xl transition-all ${form.avatar_emoji === em ? "border-zinc-950 bg-zinc-950 scale-110" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
                  >
                    {em}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {linkable.length > 0 && (
            <div className="space-y-2 border-t border-zinc-200 pt-8">
              <Label className="overline">{t("linkClone")} <span className="text-zinc-400 normal-case">(opzionale)</span></Label>
              <p className="text-xs text-zinc-500 mb-2">Collegando un clone {form.category === "personal" ? "di lavoro" : "personale"} otterrai un <strong>Clone Unificato</strong> che risponde usando entrambi i contesti.</p>
              <Select value={form.linked_clone_id || "none"} onValueChange={(v) => set("linked_clone_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="setup-link-select" className="rounded-md border-zinc-200 h-12">
                  <SelectValue placeholder="Nessuno" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {linkable.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.avatar_emoji} {c.role_name} <span className="text-zinc-500 ml-1 font-mono text-xs">· {c.category}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="pt-6 border-t border-zinc-200">
            <Button type="submit" disabled={loading} data-testid="setup-submit-btn" className="h-12 px-8 bg-zinc-950 hover:bg-zinc-800 rounded-md font-medium gap-2">
              {loading ? t("loading") : t("createAndGenerate")}
              <ArrowRight size={16} />
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
